import { Component, Input, Output } from '@angular/core';
import {
  FiltersSource,
  filtersSourceIsEnumerable,
  filtersSourceKey,
} from '@rivethealth/data/advanced-filters';
import { ObservableInput } from 'observable-input';
import { Observable, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

interface ChangeValueEvent<Filter, K extends keyof Filter> {
  /** The filter name to change. */
  name: K;
  /** Function to take the old value and determine the new value. */
  reduce: (oldValue: Filter[K]) => Filter[K];
}

@Component({
  host: {
    '[class.rv-rows]': '!inline && style',
    '[class.medium]': '!inline',
    '[class.inline]': 'inline',
  },
  selector: 'filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
})
export class FiltersComponent<Filter extends object> {
  /**
   * This is the current set of filters. It sits and waits for `changeValue`
   * to fire.
   */
  @Input()
  @ObservableInput()
  value!: Observable<Filter>;

  @Input()
  inline: boolean = false;

  @Input()
  style: boolean = true;

  /**
   * This Subject gets fired by the specific filter components themselves.
   */
  readonly changeValue = new Subject<ChangeValueEvent<Filter, keyof Filter>>();

  /**
   * This output combines the current filters with any changeValue requested
   * by specific filter components (filter-list-single, etc).
   */
  @Output()
  readonly valueChange = this.value.pipe(
    switchMap(oldFilters =>
      this.changeValue.pipe(
        map(event => {
          const newFilters = {
            ...oldFilters,
            [event.name]: event.reduce(oldFilters[event.name]),
          };
          // Individual components should only be emitting on direct user action
          // (click/typing). If you have any filter analytic events getting
          // fired by loopback noise, you can console.log `event.name`, check
          // which component is associated with that filter name, and look into
          // how it propagates inputs to output(s). AdvancedFilterMultiSelect
          // and AdvancedFilterNumber should have examples, where source
          // information (automatic vs manual) is used to make sure they only
          // emit on direct user action.

          //console.log('event.name', event.name);

          const newFiltersSource: FiltersSource = {
            source: 'byHand',
          };
          Object.defineProperty(newFilters, filtersSourceKey, {
            enumerable: filtersSourceIsEnumerable,
            value: newFiltersSource,
          });
          return newFilters;
        }),
      ),
    ),
  );
}
